/**
 * Formatting utilities for the bulk editor
 */

/**
 * Format a number with K/M abbreviations
 * @example
 * formatNumber(1234) // "1.2K"
 * formatNumber(45678) // "45.7K"
 * formatNumber(1234567) // "1.2M"
 */
export function formatNumber(num: number): string {
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)}K`;
  }
  return num.toString();
}

/**
 * Format seconds into human-readable duration
 * @example
 * formatDuration(3665) // "1h 1m"
 * formatDuration(2700) // "45m"
 * formatDuration(10800) // "3h"
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  return `${minutes}m`;
}

/**
 * Format a percentage value
 * @example
 * formatPercentage(0.8542) // "85%"
 * formatPercentage(0.125) // "12%"
 */
export function formatPercentage(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/**
 * Get color class for completion rate
 * @param rate - Completion rate between 0 and 1
 * @returns Tailwind text color class
 */
export function getCompletionColor(rate: number): string {
  if (rate >= 0.7) return "text-green-400";
  if (rate >= 0.4) return "text-yellow-400";
  return "text-red-400";
}

/**
 * Get color class for view count
 * @param views - Number of views
 * @returns Tailwind text color class
 */
export function getViewsColor(views: number): string {
  if (views >= 1000) return "text-green-400";
  if (views >= 100) return "text-yellow-400";
  return "text-neutral-400";
}
